import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  editorToText,
  joinHardLines,
  pastedHtmlToEditorHtml,
  pastedPlainToEditorHtml,
  textToEditorHtml
} from "../../utils/richText";

const PALETTE = [
  { value: "#000000", label: "Black" },
  { value: "#d32f2f", label: "Red" },
  { value: "#ef6c00", label: "Orange" },
  { value: "#2e7d32", label: "Green" },
  { value: "#1565c0", label: "Blue" },
  { value: "#6a1b9a", label: "Purple" },
  { value: "#616161", label: "Gray" }
];

const ALIGNMENTS = [
  { key: "left", command: "justifyLeft", label: "Align left" },
  { key: "center", command: "justifyCenter", label: "Align centre" },
  { key: "right", command: "justifyRight", label: "Align right" }
];

// Four text lines of alternating length, anchored left / centred / right.
const AlignIcon = ({ align }) => {
  const widths = [14, 9, 14, 9];
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      {widths.map((w, i) => {
        const x = align === "left" ? 1 : align === "right" ? 15 - w : (16 - w) / 2;
        return <rect key={i} x={x} y={2 + i * 3.6} width={w} height="1.7" rx="0.5" fill="currentColor" />;
      })}
    </svg>
  );
};

// One paragraph block of the article body: a small WYSIWYG editor with
// Bold / Italic / Text colour / Alignment. Uncontrolled after mount (the DOM is the source
// of truth while typing) so the caret never jumps; it reports the serialised
// text via `onChange` (see utils/richText.js for the format).
//
// Uses document.execCommand: deprecated on paper but the only dependency-free
// way to format a selection inside contentEditable, and supported by every
// browser the admin portal targets.
const RichTextBlock = ({ initialText, placeholder, onChange }) => {
  const editorRef = useRef(null);
  const savedRange = useRef(null);
  const [active, setActive] = useState({ bold: false, italic: false, align: "left" });

  // Seed once. Later edits come from the user, not from props.
  useEffect(() => {
    editorRef.current.innerHTML = textToEditorHtml(initialText);
  }, []);

  // Remember the selection so toolbar controls that steal focus (the native
  // colour picker) can still format the text the user selected.
  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      if (!editorRef.current || !editorRef.current.contains(sel.anchorNode)) return;
      savedRange.current = sel.getRangeAt(0).cloneRange();
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        align: document.queryCommandState("justifyCenter")
          ? "center"
          : document.queryCommandState("justifyRight")
            ? "right"
            : "left"
      });
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const text = editorToText(el);
    // Browsers leave a lone <br> behind when the editor is emptied, which
    // would hide the placeholder.
    if (!text && el.innerHTML !== "") el.innerHTML = "";
    onChange(text);
  }, [onChange]);

  const run = (command, value) => {
    const el = editorRef.current;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    // Emit <b>/<font color> elements rather than inline styles; the serialiser
    // understands both, this just keeps the DOM small. Alignment is the
    // exception: it is written as text-align on a block.
    document.execCommand("styleWithCSS", false, command.startsWith("justify"));
    document.execCommand(command, false, value);
    emit();
  };

  // Merge the hard line breaks left by text pasted from a printed column (see
  // joinHardLines). Works on the whole block.
  const joinLines = () => {
    const el = editorRef.current;
    const text = editorToText(el);
    const joined = joinHardLines(text);
    if (joined === text) return;
    el.innerHTML = textToEditorHtml(joined);
    emit();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");
    const insert = html ? pastedHtmlToEditorHtml(html) : pastedPlainToEditorHtml(plain);
    document.execCommand("insertHTML", false, insert);
    emit();
  };

  // mousedown on a toolbar button would blur the editor and drop the selection.
  const keepSelection = (e) => e.preventDefault();

  return (
    <div className="rich-block">
      <div className="rich-toolbar" role="toolbar" aria-label="Text formatting">
        <button
          type="button"
          className={`rich-btn rich-btn--bold${active.bold ? " is-active" : ""}`}
          title="Bold"
          aria-label="Bold"
          aria-pressed={active.bold}
          onMouseDown={keepSelection}
          onClick={() => run("bold")}
        >
          B
        </button>
        <button
          type="button"
          className={`rich-btn rich-btn--italic${active.italic ? " is-active" : ""}`}
          title="Italic"
          aria-label="Italic"
          aria-pressed={active.italic}
          onMouseDown={keepSelection}
          onClick={() => run("italic")}
        >
          I
        </button>
        <span className="rich-sep" />
        {PALETTE.map((c) => (
          <button
            key={c.value}
            type="button"
            className="rich-swatch"
            style={{ background: c.value }}
            title={`Text colour: ${c.label}`}
            aria-label={`Text colour ${c.label}`}
            onMouseDown={keepSelection}
            onClick={() => run("foreColor", c.value)}
          />
        ))}
        <label className="rich-custom-color" title="Custom text colour">
          <span aria-hidden="true">A</span>
          <input
            type="color"
            aria-label="Custom text colour"
            defaultValue="#d32f2f"
            onChange={(e) => run("foreColor", e.target.value)}
          />
        </label>
        <span className="rich-sep" />
        {ALIGNMENTS.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`rich-btn rich-btn--align${active.align === a.key ? " is-active" : ""}`}
            title={a.label}
            aria-label={a.label}
            aria-pressed={active.align === a.key}
            onMouseDown={keepSelection}
            onClick={() => run(a.command)}
          >
            <AlignIcon align={a.key} />
          </button>
        ))}
        <span className="rich-sep" />
        <button
          type="button"
          className="rich-btn rich-btn--join"
          title="Join broken lines: merges the line breaks left by text pasted from the e-paper"
          aria-label="Join broken lines"
          onMouseDown={keepSelection}
          onClick={joinLines}
        >
          Join lines
        </button>
        <button
          type="button"
          className="rich-btn rich-btn--clear"
          title="Clear formatting from the selection"
          aria-label="Clear formatting"
          onMouseDown={keepSelection}
          onClick={() => run("removeFormat")}
        >
          Clear
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-editor"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
      />
    </div>
  );
};

export default RichTextBlock;
